import React, { ReactNode } from 'react'

import { ErrorMessage, Field, FieldHookConfig, useField } from 'formik'

/**
 * Simple function that blur an input field when pressing "Enter" or "Esc"
 *
 * In other words, it exits the field focus, triggering the `onBlur` event
 * for submitting the form, instead of requiring the user to press the mouse pointer
 * outside the form manually ((┛ಠ_ಠ)┛彡┻━┻ outrageous user experience!)
 *
 * @param e Keyboard event when pressing a key down
 */
const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
  if (e.key === 'Enter' || e.key === 'Escape') (e.target as HTMLInputElement).blur()
}

export type InputFieldProps = {
  label?: string
  parentClassName?: string
  placeholder?: string
  style?: 'minimal' | 'default' | 'none'
  showError?: boolean
  customClasses?: string
  labelFlag?: ReactNode
  labelClasses?: string
  smallError?: boolean
} & FieldHookConfig<string>

/**
 * @deprecated Use the Formik Field and customize at the feature level
 * @see useForm() hook
 */
const InputField = (props: InputFieldProps) => {
  const {
    label,
    parentClassName,
    placeholder = '~',
    style = 'minimal',
    showError,
    customClasses,
    labelFlag,
    labelClasses,
    smallError = true,
    ...fieldHookConfig
  } = props

  const [field, meta] = useField(fieldHookConfig)
  const isDateField = props.type === 'date'

  const fieldClasses =
    style === 'none' ? '' : style === 'minimal' ? 'minimal-input' : 'classic-input'

  const errorMessage =
    meta.touched && meta.error && isDateField ? (
      <div className='text-error'>{meta.error}</div>
    ) : null

  return (
    <div className={parentClassName}>
      {!!label && (
        <label
          htmlFor={fieldHookConfig.name}
          className={`text-sm text-gray-400 mb-2 flex ${labelClasses}`}
        >
          {label}
          {labelFlag}
        </label>
      )}
      <Field
        {...field}
        {...fieldHookConfig}
        className={`${fieldClasses} ${customClasses}`}
        placeholder={placeholder}
        onKeyDown={handleKeyDown}
      />
      {showError && !isDateField && (
        <ErrorMessage
          component='span'
          name={fieldHookConfig.name}
          className={smallError ? 'label-text-alt text-red-500 ml-2' : 'text-error'}
        />
      )}
      {errorMessage}
    </div>
  )
}

export default InputField
